import { useEffect, useState, useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'kreart-theme';

function readStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore — SSR / privacy mode */
  }
  return 'system';
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

/**
 * Tiny pub/sub so multiple `useTheme` consumers stay in sync after `setTheme`.
 */
const listeners = new Set<() => void>();
let currentTheme: Theme = typeof window === 'undefined' ? 'system' : readStoredTheme();

function setStoredTheme(next: Theme): void {
  currentTheme = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Theme {
  return currentTheme;
}

function getServerSnapshot(): Theme {
  return 'system';
}

export function useTheme(): {
  theme: Theme;
  setTheme: (t: Theme) => void;
  resolvedTheme: ResolvedTheme;
} {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((next: Theme) => {
    setStoredTheme(next);
  }, []);

  return { theme, setTheme, resolvedTheme };
}

/**
 * Initialises the `dark` class from storage / system before React mounts so
 * we avoid a flash of the wrong theme. Idempotent — safe to call multiple
 * times. The `index.html` inline script also performs this synchronously to
 * cover the very first paint.
 */
export function initTheme(): void {
  if (typeof window === 'undefined') return;
  const stored = readStoredTheme();
  const resolved: ResolvedTheme = stored === 'system' ? getSystemTheme() : stored;
  applyTheme(resolved);
}
